import './bootstrap';
import { createApp, h, reactive } from 'vue';
import axios from 'axios';
import router from './router';
import App from './App.vue';
import OfflineDownloadModal from './components/OfflineDownloadModal.vue';
import '../css/app.css';
import { registerSW } from 'virtual:pwa-register';
import { initializeSyncQueue } from './composables/useSyncQueue';
import { initializeConnectivityMonitor, isConnected } from './composables/useConnectivityStatus';
import ApiService from './services/ApiService.js';
import {
    cacheOfflineImages,
    cacheOfflineMedia,
    clearOfflineMediaCache,
    dismissOfflineMediaPrompt,
    extractImageUrls,
    extractVideoUrls,
    markInstalledEventCaptured,
    shouldPromptForOfflineMedia,
} from './services/offlineMediaService';

// PrimeVue imports
import PrimeVue from 'primevue/config';
import ToastService from 'primevue/toastservice';
import ConfirmationService from 'primevue/confirmationservice';

// PrimeVue styles
import 'primeicons/primeicons.css';
import 'primeflex/primeflex.css';
import 'primeflex/themes/primeone-light.css';
import { useNavigation } from './composables/useNavigation.js';

// Configure axios
window.axios = axios;
window.axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';

// Create the Vue application
const app = createApp(App);
initializeConnectivityMonitor();

const { fetchData } = useNavigation()
let isOfflinePromptInFlight = false;

const collectTabbedComponentImageUrls = async (pages = []) => {
    const pageSlugs = [...new Set((pages || []).map((page) => page?.slug).filter(Boolean))];

    if (!pageSlugs.length) {
        return [];
    }

    const found = new Set();
    const componentResponses = await Promise.allSettled(
        pageSlugs.map((slug) => ApiService.getPageComponents(slug, 'tabbedContent'))
    );

    componentResponses.forEach((result) => {
        if (result.status !== 'fulfilled') {
            return;
        }

        const urls = extractImageUrls(result.value?.data ?? result.value ?? []);
        urls.forEach((url) => found.add(url));
    });

    return [...found];
};

const collectOfflineImageUrls = async (pages = []) => {
    const imageUrls = new Set(extractImageUrls(pages));

    if (isConnected.value) {
        const tabbedComponentImages = await collectTabbedComponentImageUrls(pages);
        tabbedComponentImages.forEach((url) => imageUrls.add(url));
    }

    imageUrls.add('/images/privacy-qr.png');

    return [...imageUrls];
};

const createOfflineDownloadModal = ({ videoCount, imageCount }) => {
    const container = document.createElement('div');
    document.body.appendChild(container);

    const state = reactive({
        videoCount,
        imageCount,
        videoCompleted: 0,
        imageCompleted: 0,
        statusText: 'Ready to download.',
        downloading: false,
        showClose: false,
    });

    let decisionResolver;
    const decisionPromise = new Promise((resolve) => {
        decisionResolver = resolve;
    });

    const choose = (value) => {
        if (!decisionResolver) {
            return;
        }

        const resolve = decisionResolver;
        decisionResolver = null;
        resolve(value);
    };

    const modalApp = createApp({
        render() {
            return h(OfflineDownloadModal, {
                videoCount: state.videoCount,
                imageCount: state.imageCount,
                videoCompleted: state.videoCompleted,
                imageCompleted: state.imageCompleted,
                statusText: state.statusText,
                downloading: state.downloading,
                showClose: state.showClose,
                onDownload: () => choose(true),
                onCancel: () => choose(false),
                onClose: () => close(),
            });
        },
    });

    modalApp.mount(container);

    const setProgress = (type, completed, total) => {
        const safeTotal = Math.max(total || 0, 0);
        const safeCompleted = Math.min(Math.max(completed || 0, 0), safeTotal || completed || 0);

        if (type === 'video') {
            state.videoCompleted = safeCompleted;
            state.videoCount = safeTotal;
            return;
        }

        state.imageCompleted = safeCompleted;
        state.imageCount = safeTotal;
    };

    const setStatus = (message) => {
        state.statusText = message;
    };

    const finish = (message) => {
        setStatus(message);
        state.downloading = false;
        state.showClose = true;
    };

    const close = () => {
        modalApp.unmount();
        container.remove();
    };

    return {
        waitForDecision: () => decisionPromise,
        setProgress,
        setStatus,
        finish,
        setDownloading: (value) => {
            state.downloading = value;
        },
        close,
    };
};

const maybePromptOfflineMediaDownload = async (pages = []) => {
    if (isOfflinePromptInFlight) {
        return;
    }

    isOfflinePromptInFlight = true;

    try {
        const videoUrls = extractVideoUrls(pages);
        const uniqueImageUrls = await collectOfflineImageUrls(pages);
        const totalAssets = videoUrls.length + uniqueImageUrls.length;

        if (!totalAssets || !shouldPromptForOfflineMedia()) {
            return;
        }

        const modal = createOfflineDownloadModal({
            videoCount: videoUrls.length,
            imageCount: uniqueImageUrls.length,
        });

        modal.setProgress('video', 0, videoUrls.length);
        modal.setProgress('image', 0, uniqueImageUrls.length);

        const wantsDownload = await modal.waitForDecision();

        if (!wantsDownload) {
            dismissOfflineMediaPrompt();
            modal.close();
            return;
        }

        modal.setStatus('Downloading offline assets...');
        modal.setDownloading(true);

        try {
            const [videoResult, imageResult] = await Promise.all([
                videoUrls.length
                    ? cacheOfflineMedia(videoUrls, (progress) => {
                        modal.setProgress('video', progress.completed, progress.total);
                    })
                    : Promise.resolve({ total: 0, successCount: 0, failed: [] }),
                uniqueImageUrls.length
                    ? cacheOfflineImages(uniqueImageUrls, (progress) => {
                        modal.setProgress('image', progress.completed, progress.total);
                    })
                    : Promise.resolve({ total: 0, successCount: 0, failed: [] }),
            ]);

            if (!videoUrls.length) {
                modal.setProgress('video', 0, 0);
            }

            if (!uniqueImageUrls.length) {
                modal.setProgress('image', 0, 0);
            }

            if (!videoUrls.length) {
                dismissOfflineMediaPrompt();
            }

            const failedCount = videoResult.failed.length + imageResult.failed.length;
            modal.finish(
                `Saved ${videoResult.successCount}/${videoResult.total} videos and ${imageResult.successCount}/${imageResult.total} images.${
                    failedCount ? ` ${failedCount} failed and may require network access.` : ''
                }`
            );
        } catch (error) {
            console.error('Offline video download failed:', error);
            modal.finish('Unable to download offline assets right now. Please try again with a stable connection.');
        }
    } finally {
        isOfflinePromptInFlight = false;
    }
};

const syncOfflineImagesInBackground = async (pages = []) => {
    if (!isConnected.value) {
        return;
    }

    try {
        const imageUrls = await collectOfflineImageUrls(pages);
        if (!imageUrls.length) {
            return;
        }

        await cacheOfflineImages(imageUrls);
    } catch (error) {
        console.warn('Background offline image refresh failed:', error);
    }
};

const triggerOfflinePromptWithRetries = (pages = []) => {
    const tryPrompt = () => {
        void maybePromptOfflineMediaDownload(pages);
    };

    tryPrompt();
    setTimeout(tryPrompt, 500);
    setTimeout(tryPrompt, 1500);

    window.addEventListener('pageshow', tryPrompt, { once: true });
};

const waitForServiceWorkerControl = async (timeoutMs = 2500) => {
    if (!('serviceWorker' in navigator) || navigator.serviceWorker.controller) {
        return;
    }

    await Promise.race([
        new Promise((resolve) => {
            navigator.serviceWorker.addEventListener('controllerchange', resolve, { once: true });
        }),
        new Promise((resolve) => setTimeout(resolve, timeoutMs)),
    ]);
};

// Use PrimeVue and its services
app.use(PrimeVue, { ripple: true });
app.use(ToastService);
app.use(ConfirmationService);

registerSW({ immediate: true });

window.addEventListener('appinstalled', () => {
    markInstalledEventCaptured();
});

const bootstrapApp = async () => {
    await waitForServiceWorkerControl();
    const navigationData = await fetchData();

    app.use(router);

    initializeSyncQueue();
    
    // Mount the app
    const vm = app.mount('#app');
    
    // Expose navigation functions globally
    window.appNavigation = {
        goToPage: (slugOrIndex) => {
            // Handle both slug strings and numeric indices
            if (typeof slugOrIndex === 'string') {
                // It's a slug, navigate directly
                router.push(`/pages/${slugOrIndex}`);
            } else {
                // It's an index, find the page and navigate
                const page = vm.pagesList?.value?.[slugOrIndex];
                if (page) {
                    router.push(`/pages/${page.slug}`);
                }
            }
        }
    };

    window.offlineMedia = {
        download: () => maybePromptOfflineMediaDownload(navigationData?.pages || []),
        clear: () => clearOfflineMediaCache(),
        refreshImages: () => syncOfflineImagesInBackground(navigationData?.pages || []),
    };

    void syncOfflineImagesInBackground(navigationData?.pages || []);
    triggerOfflinePromptWithRetries(navigationData?.pages || []);
};

void bootstrapApp();
